import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { SectionTitleProps, SectionTitle } from "./SectionTitle";
import { IMAGE_QUALITY } from "./constants";
import {
  ImageProps,
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
  BackgroundOverlayOptionType,
  BackgroundOverlayColorType,
} from "./types";
import { sectionWrapper } from "./sectionWrappers";

const backgroundOverlayStyle = tv({
  base: "uipm:absolute uipm:inset-0",
  variants: {
    overlayColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "accent-light": "uipm:bg-[var(--surface-surface-accent-light)]",
    },
    overlayOpacity: {
      "brand-10": "uipm:opacity-10",
      "brand-20": "uipm:opacity-20",
      "brand-30": "uipm:opacity-30",
      "brand-40": "uipm:opacity-40",
      "brand-50": "uipm:opacity-50",
      "brand-60": "uipm:opacity-60",
      "brand-70": "uipm:opacity-70",
      "brand-80": "uipm:opacity-80",
      "brand-90": "uipm:opacity-90",
      "brand-full": "uipm:opacity-100",
      "brand-empty": "uipm:opacity-0",
    },
  },
  defaultVariants: {
    overlayColor: "primary",
    overlayOpacity: "brand-50",
  },
});

export interface HeroFramedProps extends Omit<ComponentProps<"div">, "title"> {
  image?: ImageProps;
  backgroundOverlay?: BackgroundOverlayOptionType;
  backgroundOverlayColor?: BackgroundOverlayColorType;
  imageAlignment?: "top" | "middle" | "bottom";
  title?: SectionTitleProps;
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
}

export const HeroFramed: React.FC<HeroFramedProps> = ({
  image,
  backgroundOverlay = "brand-50",
  backgroundOverlayColor = "primary",
  imageAlignment = "middle",
  title,
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  className,
  id,
  ...props
}) => {
  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto">
        <div
          className={cn(
            "uipm:relative uipm:font-normal uipm:rounded-[var(--radius-radius-lg)] uipm:overflow-hidden",
            className,
          )}
          {...props}
        >
          {/* Background Image */}
          {image && (
            <div className="uipm:absolute uipm:inset-0">
              <Image
                src={image.src}
                alt={parseAiAlt(image.alt).cleanAlt}
                fill
                quality={IMAGE_QUALITY}
                className={cn(
                  "uipm:object-cover uipm:w-full uipm:h-full",
                  imageAlignment === "top" && "uipm:object-top",
                  imageAlignment === "middle" && "uipm:object-center",
                  imageAlignment === "bottom" && "uipm:object-bottom",
                )}
                sizes="100vw"
                fetchPriority="high"
                priority
              />
              <AiDisclaimerForAlt alt={image.alt} src={image.src} />
            </div>
          )}
          {/* Background Overlay */}
          <div
            className={backgroundOverlayStyle({
              overlayColor: backgroundOverlayColor,
              overlayOpacity: backgroundOverlay,
            })}
          />
          {/* Content Container */}
          <div
            className={cn(
              "uipm:relative uipm:flex uipm:flex-col uipm:min-h-[400px]",
              "uipm:px-[var(--container-margin)] uipm:py-[var(--container-card-large-padding)]",
              "uipm:md:px-[96px] uipm:md:py-[96px]",
              "uipm:lg:px-[96px] uipm:lg:py-[96px]",
              imageAlignment === "top" && "uipm:justify-start",
              imageAlignment === "middle" && "uipm:justify-center",
              imageAlignment === "bottom" && "uipm:justify-end",
            )}
          >
            <div className="uipm:relative uipm:z-10">
              {/* SectionTitle */}
              {title && (
                <SectionTitle
                  {...title}
                  className={cn(
                    "uipm:font-normal uipm:max-w-[790px] ",
                    title.className,
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
